import React, { useState, useMemo } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { Box, CircularProgress, Typography, TextField } from "@mui/material";
import "../TrucksTable/TrucksTable.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { unassignedTrailersTableColumns } from "../../../data/FormColumns/Fleet/unassignedTrailersTableColumns";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchUnassignedTrailers = async (token: string | null | undefined) => {
  const { data } = await axios.get(`${apiVariable}/getAllUnassignedTrailers`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return data;
};

const UnassignedTrailersTable = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get("page") || "0", 10);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: unassignedTrailersTable,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["unassignedTrailersTable"],
    queryFn: () => fetchUnassignedTrailers(token),
  });

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: newPage.toString() });
  };

  const filteredTrailers = useMemo(() => {
    if (!unassignedTrailersTable) return [];
    return unassignedTrailersTable.filter((trailer: any) =>
      [trailer.unitNumber, trailer.vin, trailer.licencePlate].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [unassignedTrailersTable, searchTerm]);

  const onRowClick = (params: any, event: React.MouseEvent) => {
    const url = `trailer/${params.row.id}`;

    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      event.preventDefault();
      event.stopPropagation();

      window.open("assets/" + url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <Box className="trucks-table" height={900}>
      <Box
        className="table-controls"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
          marginBottom: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", sm: 400 },
            minWidth: 0,
            alignItems: "flex-end",
            position: "relative",
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by Unit Number, VIN, Licence Plate"
            sx={{ maxWidth: "100%", width: 400 }}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {(error as any).message}</Typography>
        </Box>
      ) : (
        <PersistedDataGrid
          tableName="unassignedTrailers"
          rows={filteredTrailers}
          columns={unassignedTrailersTableColumns}
          pageSizeOptions={[10]}
          paginationModel={{ page: pageFromUrl, pageSize: 10 }}
          paginationMode="client"
          onPaginationModelChange={(m) => handlePageChange(m.page)}
          onRowClick={onRowClick}
          sx={{ height: 700 }}
        />
      )}
    </Box>
  );
};

export default UnassignedTrailersTable;
